import React, { useMemo } from 'react';
import type { ImageProps, ImageSourcePropType } from 'react-native';
import { Image as RNImage, Platform } from 'react-native';
import { Image } from './Image';
import { CloudinaryImage } from './CloudinaryImage';
import { ContentfulImage } from './ContentfulImage';

// Loads remote images through cloudinary and falls back to local image.
export const FallbackImage = React.memo(
  ({
    baseUrl,
    testID: baseTestID,
    defaultImage,
    resizeMode,
    style,
    cloudinaryConfig,
    contentfulOptions,
    ...rest
  }: FallbackImageProps) => {
    // Image Error
    const [imageLoadError, setImageLoadError] = React.useState(false);
    const handleImageLoadError = React.useCallback(() => {
      setImageLoadError(true);
    }, [setImageLoadError]);

    const isRemote = baseUrl && !imageLoadError;

    const imageProps: ImageProps = useMemo(
      () => ({
        style,
        resizeMode,
        onError: handleImageLoadError,
        testID: baseTestID?.concat(isRemote ? '-remote' : '-local'),
        ...rest,
      }),
      [baseTestID, handleImageLoadError, isRemote, resizeMode, rest, style],
    );

    // When Contentful options are specified, load remote image from Contentful
    if (isRemote && contentfulOptions) {
      return (
        <ContentfulImage
          baseUrl={baseUrl}
          options={contentfulOptions}
          {...imageProps}
          testID={baseTestID?.concat('.contentful')}
        />
      );
    }

    // When Cloudinary config is specified, load remote image from Contentful
    if (isRemote && cloudinaryConfig) {
      return (
        <CloudinaryImage
          baseUrl={baseUrl}
          config={cloudinaryConfig}
          {...imageProps}
          testID={baseTestID?.concat('.cloudinary')}
        />
      );
    }

    // return null if remote image could not be loaded and no default image was specified
    if (!isRemote && !defaultImage) {
      return null;
    }

    // load image directly if neither Cloudinary config nor Contentful options were specified,
    // or use default image if remote image could not be loaded
    return (
      <Image
        source={isRemote ? { uri: baseUrl } : defaultImage}
        {...imageProps}
        accessibilityIgnoresInvertColors
        testID={baseTestID?.concat(isRemote ? '.remote' : '.default')}
      />
    );
  },
);

if (Platform.OS === 'web') {
  // eslint-disable-next-line functional/immutable-data
  RNImage.resolveAssetSource = (source) => {
    return {
      uri: String(source),
      width: 1,
      height: 1,
      scale: 1,
    };
  };
}

type FallbackImageProps = {
  readonly baseUrl: string;
  readonly defaultImage?: ImageSourcePropType;
  readonly contentfulOptions?: React.ComponentProps<
    typeof ContentfulImage
  >['options'];
  readonly cloudinaryConfig?: React.ComponentProps<
    typeof CloudinaryImage
  >['config'];
} & ImageProps;
